/**
 * Motor de temas: sistema / claro / oscuro.
 * El primer paint lo resuelve el script inline del <head>; este módulo
 * mantiene el estado, la persistencia y los controles de la UI.
 */

export type ThemePreference = 'system' | 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'sp_theme'
const ORDER: ThemePreference[] = ['system', 'light', 'dark']

const META: Record<ThemePreference, { icon: string; label: string }> = {
  system: { icon: 'fa-circle-half-stroke', label: 'Tema del sistema' },
  light: { icon: 'fa-sun', label: 'Tema claro' },
  dark: { icon: 'fa-moon', label: 'Tema oscuro' },
}

const media = () => window.matchMedia('(prefers-color-scheme: light)')

export function getPreference(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch { /* almacenamiento no disponible */ }
  return 'system'
}

export function resolveTheme(pref: ThemePreference = getPreference()): ResolvedTheme {
  if (pref === 'light' || pref === 'dark') return pref
  return media().matches ? 'light' : 'dark'
}

function updateMetaColor(theme: ResolvedTheme) {
  const color = theme === 'light' ? '#fafbfc' : '#0d1014'
  document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach(m => {
    if (!m.dataset.dynamic) m.dataset.dynamic = '1'
    m.setAttribute('content', color)
  })
}

function paint(pref: ThemePreference) {
  const theme = resolveTheme(pref)
  const root = document.documentElement
  root.dataset.theme = theme
  root.dataset.themePreference = pref
  root.style.colorScheme = theme
  updateMetaColor(theme)
  syncToggles(pref, theme)
}

export function setPreference(pref: ThemePreference) {
  try { localStorage.setItem(STORAGE_KEY, pref) } catch { /* noop */ }
  paint(pref)
}

export function cycleTheme(): ThemePreference {
  const current = getPreference()
  const next = ORDER[(ORDER.indexOf(current) + 1) % ORDER.length]
  setPreference(next)
  return next
}

function syncToggles(pref: ThemePreference, theme: ResolvedTheme) {
  const meta = META[pref]
  const next = ORDER[(ORDER.indexOf(pref) + 1) % ORDER.length]
  const text = `${meta.label} (${theme === 'light' ? 'claro' : 'oscuro'}). Cambiar a ${META[next].label.toLowerCase()}.`
  document.querySelectorAll<HTMLButtonElement>('[data-theme-toggle]').forEach(btn => {
    const icon = btn.querySelector('i')
    if (icon) icon.className = `fas ${meta.icon}`
    btn.setAttribute('aria-label', text)
    btn.setAttribute('title', text)
    btn.dataset.themeState = pref
  })
}

let initialized = false

export function initTheme() {
  if (initialized) return
  initialized = true

  paint(getPreference())

  // Solo reacciona al sistema cuando la preferencia es "system"
  const onSchemeChange = () => { if (getPreference() === 'system') paint('system') }
  if (media().addEventListener) media().addEventListener('change', onSchemeChange)
  else media().addListener(onSchemeChange)

  // Sincroniza entre pestañas
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) paint(getPreference())
  })

  document.querySelectorAll<HTMLElement>('[data-theme-toggle]').forEach(el => {
    el.addEventListener('click', () => cycleTheme())
  })

  syncToggles(getPreference(), resolveTheme())
}
